import * as React from 'react'
import {
  NativeSyntheticEvent,
  Platform,
  Text,
  TextInput,
  TextInputFocusEventData,
  TextInputProps,
  TouchableOpacity,
  View,
} from 'react-native'
import styled from '@theme/styled-components'

import Icon from '@components/icon/Icons'

import useBreakpoints from '@utils/useBreakpoints'

import theme from '@theme/Theme'

interface ButtonIcon {
  name: IconName
  onClick?: () => void
  disabled?: boolean
}

type SearchStyle = 'onLight' | 'onDark'

interface Props extends Omit<TextInputProps, 'onChange'> {
  disabled?: boolean
  error?: string
  hint?: string
  left?: ButtonIcon
  right?: ButtonIcon
  searchStyle?: SearchStyle
  title?: string
  inputRef?: React.RefObject<TextInput>
  onChange: (val: string) => void
}

const TextInputComponent = ({
  disabled = false,
  error,
  hint,
  left,
  right,
  searchStyle,
  title,
  inputRef,
  onChange,
  ...inputProps
}: Props) => {
  const [, bp] = useBreakpoints()
  const isPhone = bp === 'phone'

  const [isFocused, setIsFocused] = React.useState<boolean>(false)

  const onFocus = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
    setIsFocused(true)

    if (inputProps.onFocus) {
      inputProps.onFocus(e)
    }
  }

  const onBlur = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
    setIsFocused(false)

    if (inputProps.onBlur) {
      inputProps.onBlur(e)
    }
  }

  const getColor = (icon: ButtonIcon) =>
    searchStyle && isPhone
      ? searchStyle === 'onDark'
        ? theme.colors.background
        : theme.colors.tertiary
      : disabled || icon.disabled
      ? theme.colors.primaryFocused
      : error
      ? theme.colors.negate
      : theme.colors.primary

  const placeholderTextColor =
    searchStyle && isPhone
      ? searchStyle === 'onDark'
        ? theme.colors.backgroundTertiary
        : theme.colors.tertiary
      : theme.colors.secondaryText

  return (
    <View>
      <InputContainer disabled={disabled} isFocused={!disabled && isFocused} searchStyle={searchStyle}>
        {!!left && (
          <IconTouchable activeOpacity={0.9} onPress={left.onClick} disabled={left.disabled || !left.onClick}>
            <Icon name={left.name} size={24} color={getColor(left)} />
          </IconTouchable>
        )}

        <FieldContainer>
          {isPhone && !!title && (
            <Title disabled={disabled} error={!!error}>
              {title}
            </Title>
          )}
          <CustomTextInput
            {...inputProps}
            ref={inputRef}
            editable={!disabled}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholderTextColor={placeholderTextColor}
            searchStyle={searchStyle}
            multiline={!!inputProps.numberOfLines && inputProps.numberOfLines > 1}
            textAlignVertical={!!inputProps.numberOfLines && inputProps.numberOfLines > 1 ? 'top' : 'center'}
            onChangeText={onChange}
          />
        </FieldContainer>

        {!!inputProps.value && (
          <ClearButton activeOpacity={0.9} onPress={() => onChange('')}>
            <Icon
              name={isPhone ? 'cancel' : 'close'}
              color={disabled ? theme.colors.primaryFocused : theme.colors.primary}
              size={19}
            />
          </ClearButton>
        )}

        {!!right && (
          <IconTouchable activeOpacity={0.9} onPress={right.onClick} disabled={right.disabled || !right.onClick} right>
            <Icon name={right.name} size={24} color={getColor(right)} />
          </IconTouchable>
        )}
      </InputContainer>

      {(!!error || (isPhone && !!hint)) && <Hint error={!!error}>{error || hint}</Hint>}
    </View>
  )
}

export default TextInputComponent

const InputContainer = styled(View)<{ disabled: boolean; isFocused: boolean; searchStyle?: SearchStyle }>`
  ${(props) => !props.disabled && `background-color: ${props.theme.colors.background};`}
  min-height: ${(props) => (props.theme.breakpoint === 'phone' ? 48 : 56)}px;
  padding: 4px 16px;
  border-bottom-width: ${(props) => (props.isFocused ? 2 : 1)}px;
  border-color: ${(props) => (props.isFocused ? props.theme.colors.primary : props.theme.colors.primaryFocused)};
  flex-direction: row;
  align-items: center;

  ${(props) =>
    props.theme.breakpoint !== 'phone' &&
    ` border-width: ${props.isFocused ? 2 : 1}px;
      border-radius: 6px;
      padding: 14px 16px;
    `}

  ${(props) =>
    props.theme.breakpoint === 'phone' &&
    !!props.searchStyle &&
    `
      height: 50px;
      padding: 6px 8px;
      border-radius: 10px;
      flex-direction: row;
      border-width: 0px;
      border-bottom-width: 0px;
      background-color: rgba(118, 118, 118, 0.13);
    `}
`

const FieldContainer = styled(View)`
  flex: 1;
`

const Title = styled(Text)<{ disabled: boolean; error: boolean }>`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) =>
    props.disabled
      ? props.theme.colors.primaryFocused
      : props.error
      ? props.theme.colors.negate
      : props.theme.colors.primary};
`

const CustomTextInput = styled(TextInput)<{ editable: boolean; searchStyle?: SearchStyle }>`
  ${(props) => props.theme.fonts.Body};
  ${(props) =>
    Platform.OS === 'web' &&
    `
    outline-width: 0px;
    outline-style: none;
    caret-color: ${
      props.theme.breakpoint === 'phone' && props.searchStyle
        ? props.theme.colors.backgroundTertiary
        : props.theme.colors.primary
    };`}
  padding: 0;
  ${(props) =>
    Platform.OS === 'ios' &&
    props.numberOfLines &&
    props.numberOfLines > 1 &&
    `min-height: ${props.numberOfLines * 12}px;`};
  color: ${(props) =>
    props.theme.breakpoint === 'phone' && props.searchStyle
      ? props.searchStyle === 'onDark'
        ? props.theme.colors.background
        : props.theme.colors.primaryText
      : props.editable
      ? props.theme.colors.primaryText
      : props.theme.colors.disableDark};
`

const Hint = styled(Text)<{ error: boolean }>`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => (props.error ? props.theme.colors.negate : props.theme.colors.secondaryText)};
  margin-top: 8px;
`
const ClearButton = styled(TouchableOpacity)`
  ${(props) => props.theme.breakpoint === 'phone' && `padding: 10px 0px 10px 10px;`}
`

const IconTouchable = styled(TouchableOpacity)<{ right?: boolean }>`
  ${(props) => (props.right ? 'padding-left: 8px' : 'padding-right: 8px')};
`
